import { useEffect, useMemo, useState } from 'react';
import { Play, RefreshCw, FlaskConical, AlertTriangle, CheckCircle2, XCircle, FileText, User, Cloud, Mail, Eye } from 'lucide-react';
import Pill from '../components/ui/Pill.jsx';
import Modal from '../components/ui/Modal.jsx';
import PdfPreviewModal from '../components/PdfPreviewModal.jsx';
import { api } from '../api/client.js';
import { useGenerationStream } from '../hooks/useGenerationStream.js';
import {
  formatMonthFr,
  presetCurrentMonth,
  presetPreviousMonth,
} from '../lib/dates.js';

// Mapping filtre UI → nom du département Synolog
const BL_FILTERS = [
  { key: 'all', label: 'Tous', department: null },
  { key: 'data', label: 'BL Data Management', department: 'Opération' },
  { key: 'conseil', label: 'BL Conseil', department: 'Métier' },
  { key: 'delivery', label: 'Delivery', department: 'Delivery' },
];

const LEVEL_STYLES = {
  job_started: { color: 'var(--primary)', icon: Play },
  consultant_start: { color: 'var(--text-muted)', icon: User },
  consultant_done: { color: 'var(--text-muted)', icon: CheckCircle2 },
  project_start: { color: 'var(--text-muted)', icon: FileText },
  pdf_generated: { color: 'var(--success)', icon: FileText },
  warning: { color: 'var(--warning)', icon: AlertTriangle },
  error: { color: 'var(--danger)', icon: XCircle },
  job_completed: { color: 'var(--success)', icon: CheckCircle2 },
  job_failed: { color: 'var(--danger)', icon: XCircle },
};

export default function GenerationView({ navigate }) {
  const initial = presetCurrentMonth();
  const [startDate, setStartDate] = useState(initial.startDate);
  const [endDate, setEndDate] = useState(initial.endDate);
  const [people, setPeople] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [blFilter, setBlFilter] = useState('all');
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [dryRun, setDryRun] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generatedCras, setGeneratedCras] = useState([]);
  const [pdfPreviewId, setPdfPreviewId] = useState(null);

  const { events, status, progress } = useGenerationStream(jobId);

  // Récupère la liste des CRA générés une fois le job terminé
  useEffect(() => {
    if (!jobId || status !== 'completed') return;
    api.cra
      .getJob(jobId)
      .then((job) => setGeneratedCras(job.craFiles ?? []))
      .catch(() => setGeneratedCras([]));
  }, [jobId, status]);

  // Charge les consultants Synolog au montage
  useEffect(() => {
    api.cra
      .listPeople()
      .then((data) => setPeople(data.people ?? []))
      .catch((err) => setLoadError(err.message));
  }, []);

  // Liste triée des tags distincts présents dans la population
  const availableTags = useMemo(() => {
    const set = new Set();
    for (const p of people) {
      for (const t of p.tags ?? []) set.add(t);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [people]);

  const filteredPeople = useMemo(() => {
    const lower = filterText.trim().toLowerCase();
    const blDept = BL_FILTERS.find((b) => b.key === blFilter)?.department ?? null;
    return people.filter((p) => {
      if (showOnlyActive) {
        if (!p.active) return false;
        // Un consultant dont la date de fin est antérieure au début de la période
        // n'est plus pertinent (ex: Aboubakar Koné parti en mars).
        if (p.endDate && startDate && p.endDate < startDate) return false;
      }
      if (blDept && p.department !== blDept) return false;
      // Tags : intersection — il faut que le consultant ait TOUS les tags
      // sélectionnés. Si aucun tag sélectionné, pas de filtre.
      if (selectedTags.size > 0) {
        const personTags = new Set(p.tags ?? []);
        for (const t of selectedTags) {
          if (!personTags.has(t)) return false;
        }
      }
      if (!lower) return true;
      return (
        p.name.toLowerCase().includes(lower) ||
        (p.email ?? '').toLowerCase().includes(lower)
      );
    });
  }, [people, filterText, showOnlyActive, blFilter, startDate, selectedTags]);

  const toggleTag = (tag) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const toggleConsultant = (email) => {
    setSelectedEmails((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email],
    );
  };

  const openConfirm = async () => {
    setPreviewLoading(true);
    setPreview(null);
    try {
      const { previews } = await api.cra.preview({
        startDate,
        endDate,
        consultantEmails: selectedEmails,
      });
      setPreview(previews);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setPreviewLoading(false);
    }
    setConfirmOpen(true);
  };

  const handleStart = async () => {
    setConfirmOpen(false);
    try {
      const { jobId: newId } = await api.cra.startJob({
        startDate,
        endDate,
        consultantEmails: selectedEmails,
        dryRun,
      });
      setJobId(newId);
    } catch (err) {
      setLoadError(err.message);
    }
  };

  const isRunning = status === 'streaming';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Période</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <Pill
            onClick={() => {
              const p = presetCurrentMonth();
              setStartDate(p.startDate);
              setEndDate(p.endDate);
            }}
          >
            Mois courant
          </Pill>
          <Pill
            onClick={() => {
              const p = presetPreviousMonth();
              setStartDate(p.startDate);
              setEndDate(p.endDate);
            }}
          >
            Mois précédent
          </Pill>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <label style={labelStyle}>
            Date de début
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            Date de fin
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={inputStyle}
            />
          </label>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
          Période active : <strong>{formatMonthFr(startDate)}</strong> au{' '}
          <strong>{formatMonthFr(endDate)}</strong>
        </p>
      </section>

      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          Consultants ({filteredPeople.length} affichés, {selectedEmails.length} sélectionnés)
        </h2>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          {BL_FILTERS.map((bl) => (
            <Pill
              key={bl.key}
              active={blFilter === bl.key}
              onClick={() => setBlFilter(bl.key)}
            >
              {bl.label}
            </Pill>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          <input
            type="search"
            placeholder="Filtrer par nom ou email…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            style={{ ...inputStyle, flex: '1 1 240px' }}
          />
          <Pill active={showOnlyActive} onClick={() => setShowOnlyActive((v) => !v)}>
            Actifs uniquement
          </Pill>
          <Pill
            onClick={() =>
              setSelectedEmails(filteredPeople.map((p) => p.email).filter(Boolean))
            }
            disabled={filteredPeople.length === 0}
          >
            Sélectionner tous
          </Pill>
          <Pill
            onClick={() => setSelectedEmails([])}
            disabled={selectedEmails.length === 0}
          >
            Désélectionner tout
          </Pill>
        </div>

        {availableTags.length > 0 && (
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginRight: '0.25rem' }}>
              Tags :
            </span>
            {availableTags.map((tag) => (
              <Pill key={tag} active={selectedTags.has(tag)} onClick={() => toggleTag(tag)}>
                {tag}
              </Pill>
            ))}
            {selectedTags.size > 0 && (
              <Pill onClick={() => setSelectedTags(new Set())}>Effacer tags</Pill>
            )}
          </div>
        )}

        {loadError && (
          <p style={{ color: 'var(--danger)' }}>Erreur : {loadError}</p>
        )}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.4rem',
            maxHeight: '320px',
            overflowY: 'auto',
            padding: '0.5rem',
            background: 'var(--glass)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {filteredPeople.map((p) => (
            <Pill
              key={p.email}
              active={selectedEmails.includes(p.email)}
              onClick={() => toggleConsultant(p.email)}
            >
              {p.name}
            </Pill>
          ))}
          {filteredPeople.length === 0 && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Aucun consultant ne correspond aux filtres.
            </p>
          )}
        </div>

        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
          Aucun sélectionné = tous les actifs avec email seront traités.
        </p>
      </section>

      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Lancement</h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Pill active={dryRun} onClick={() => setDryRun((v) => !v)} icon={FlaskConical}>
            Mode dry-run (simulation)
          </Pill>
          <button
            onClick={openConfirm}
            disabled={isRunning || !startDate || !endDate || previewLoading}
            style={primaryButtonStyle(isRunning || previewLoading)}
          >
            {(isRunning || previewLoading) ? <RefreshCw size={16} className="spin" /> : <Play size={16} />}
            {isRunning ? 'Génération en cours…' : previewLoading ? 'Vérification…' : 'Lancer la génération'}
          </button>
        </div>

        {progress.total > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div
              style={{
                height: '8px',
                background: 'var(--glass)',
                borderRadius: 'var(--radius-pill)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${(progress.done / Math.max(progress.total, 1)) * 100}%`,
                  height: '100%',
                  background: 'var(--primary)',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
              {progress.done} / {progress.total} consultants traités
            </p>
          </div>
        )}
      </section>

      {jobId && status === 'completed' && (
        <section
          style={{
            ...cardStyle,
            border: '1px solid var(--success)',
            background: 'linear-gradient(180deg, rgba(34,197,94,0.08), var(--card-bg))',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <CheckCircle2 size={22} color="var(--success)" />
            <h2 style={{ ...sectionTitleStyle, margin: 0, color: 'var(--success)' }}>
              Génération terminée
            </h2>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 0 }}>
            Que voulez-vous faire avec les CRA générés ?
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            <button
              onClick={() => navigate?.('drive', { jobId })}
              style={primaryButtonStyle(false)}
            >
              <Cloud size={16} /> Upload vers Google Drive
            </button>
            <button
              onClick={() => navigate?.('mail', { jobId })}
              style={primaryButtonStyle(false)}
            >
              <Mail size={16} /> Envoyer aux collaborateurs
            </button>
            <button onClick={() => navigate?.('history')} style={secondaryButtonStyle}>
              Voir l'historique
            </button>
          </div>
        </section>
      )}

      {generatedCras.length > 0 && (
        <section style={cardStyle}>
          <h2 style={sectionTitleStyle}>
            CRA générés ({generatedCras.length})
          </h2>
          <div
            style={{
              maxHeight: '320px',
              overflowY: 'auto',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            {generatedCras.map((cra) => (
              <div
                key={cra.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.6rem 0.9rem',
                  borderBottom: '1px solid var(--glass-border)',
                }}
              >
                <FileText size={16} color="var(--text-muted)" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{cra.consultantName}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {cra.clientName} · {cra.projectName} · {cra.totalDays} j
                  </div>
                </div>
                <button
                  onClick={() => setPdfPreviewId(cra.id)}
                  title="Prévisualiser le PDF"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    padding: '0.35rem 0.7rem',
                    background: 'transparent',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                  }}
                >
                  <Eye size={14} /> Aperçu
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <PdfPreviewModal
        craFileId={pdfPreviewId}
        onClose={() => setPdfPreviewId(null)}
      />

      {events.length > 0 && (
        <section style={cardStyle}>
          <h2 style={sectionTitleStyle}>Journal d'exécution</h2>
          <div
            style={{
              maxHeight: '360px',
              overflowY: 'auto',
              background: 'var(--glass)',
              borderRadius: 'var(--radius-md)',
              padding: '0.75rem',
              fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
              fontSize: '0.82rem',
            }}
          >
            {events.map((e, i) => {
              const style = LEVEL_STYLES[e.type] ?? { color: 'var(--text)', icon: FileText };
              const Icon = style.icon;
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    padding: '0.3rem 0',
                    borderBottom: '1px solid var(--glass-border)',
                    color: style.color,
                  }}
                >
                  <Icon size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <strong>{e.type}</strong>
                    {e.consultantName && ` — ${e.consultantName}`}
                    {e.projectName && ` — ${e.projectName}`}
                    {e.message && <div style={{ color: 'var(--text-muted)' }}>{e.message}</div>}
                    {e.error && <div style={{ color: 'var(--danger)' }}>{e.error}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirmer la génération"
        size="lg"
        footer={
          <>
            <button onClick={() => setConfirmOpen(false)} style={secondaryButtonStyle}>
              Annuler
            </button>
            <button onClick={handleStart} style={primaryButtonStyle(false)}>
              <Play size={16} />
              {dryRun ? 'Lancer la simulation' : 'Lancer la génération'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p>Vous êtes sur le point de lancer une génération avec les paramètres suivants :</p>
          <ul style={{ paddingLeft: '1.25rem', margin: 0 }}>
            <li>
              Période : <strong>{startDate}</strong> → <strong>{endDate}</strong>
            </li>
            <li>
              Mode :{' '}
              <strong>{dryRun ? 'Dry-run (aucun PDF écrit)' : 'Production (PDFs générés)'}</strong>
            </li>
          </ul>

          {preview && (
            <div style={{ marginTop: '0.5rem' }}>
              <strong style={{ color: 'var(--primary)' }}>
                Pré-validation Synolog ({preview.length} consultants concernés)
              </strong>
              <div style={{
                marginTop: '0.5rem',
                maxHeight: '320px',
                overflowY: 'auto',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-md)',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead style={{ background: 'var(--glass)' }}>
                    <tr>
                      <th style={previewThStyle}>Consultant</th>
                      <th style={{ ...previewThStyle, textAlign: 'right' }} title="Capacité nette Synolog : jours ouvrés − fériés du canton − congés validés">Attendu</th>
                      <th style={{ ...previewThStyle, textAlign: 'right' }} title="Jours saisis, toutes affaires confondues (intercontrat compris)">Saisi</th>
                      <th style={{ ...previewThStyle, textAlign: 'right' }} title="Jours attendus non saisis">Manquant</th>
                      <th style={{ ...previewThStyle, textAlign: 'right' }} title="Jours saisis sur des affaires facturables : contenu des CRA">Facturable</th>
                      <th style={{ ...previewThStyle, textAlign: 'right' }} title="Nombre de CRA qui seront générés">CRA</th>
                      <th style={previewThStyle}>Anomalies</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((p) => {
                      const missingColor =
                        p.missingDays == null
                          ? 'var(--text-muted)'
                          : p.missingDays > 0
                            ? 'var(--danger)'
                            : 'var(--success)';
                      return (
                        <tr key={p.peopleId} style={{ borderTop: '1px solid var(--glass-border)' }}>
                          <td style={previewTdStyle}>{p.name}</td>
                          <td style={{ ...previewTdStyle, textAlign: 'right', color: 'var(--text-muted)' }}>
                            {p.expectedDays != null ? p.expectedDays.toFixed(1) : '—'}
                          </td>
                          <td style={{ ...previewTdStyle, textAlign: 'right' }}>
                            {p.loggedDays.toFixed(1)}
                          </td>
                          <td style={{ ...previewTdStyle, textAlign: 'right', color: missingColor, fontWeight: 500 }}>
                            {p.missingDays != null ? p.missingDays.toFixed(1) : '—'}
                          </td>
                          <td style={{ ...previewTdStyle, textAlign: 'right' }}>
                            {p.billableDays.toFixed(1)}
                          </td>
                          <td style={{ ...previewTdStyle, textAlign: 'right' }}>{p.craCount}</td>
                          <td style={previewTdStyle}>
                            {p.warnings.length === 0 ? (
                              <span style={{ color: 'var(--success)', fontSize: '0.75rem' }}>OK</span>
                            ) : (
                              <ul style={{ margin: 0, paddingLeft: '1rem', color: 'var(--warning)', fontSize: '0.75rem' }}>
                                {p.warnings.map((w, i) => (
                                  <li key={i}>{w}</li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {preview.some((p) => p.warnings.length > 0) && (
                <p style={{ color: 'var(--warning)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  ⚠️ {preview.filter((p) => p.warnings.length > 0).length} consultant(s) avec anomalies — vérifiez Synolog avant de lancer.
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

// =====================================================================
// Styles inline partagés (constants pour ne pas créer à chaque render)
// =====================================================================

const cardStyle = {
  background: 'var(--card-bg)',
  padding: '1.5rem',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-card)',
};

const sectionTitleStyle = {
  margin: '0 0 1rem 0',
  fontSize: '1rem',
  color: 'var(--primary)',
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  color: 'var(--text-muted)',
  fontSize: '0.85rem',
};

const inputStyle = {
  padding: '0.5rem 0.75rem',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--glass-border)',
  background: 'var(--glass)',
  color: 'var(--text)',
  fontSize: '0.9rem',
};

const primaryButtonStyle = (disabled) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.6rem 1.25rem',
  background: disabled ? 'var(--text-muted)' : 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
});

const secondaryButtonStyle = {
  padding: '0.6rem 1.25rem',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: '0.9rem',
};

const previewThStyle = {
  padding: '0.5rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  color: 'var(--text-muted)',
  fontSize: '0.8rem',
};
const previewTdStyle = {
  padding: '0.4rem 0.75rem',
  verticalAlign: 'top',
};
